/**
 * Что сказать человеку, когда сервер отказал.
 *
 * Сервер не знает языка читателя и отдаёт в `detail` английскую фразу для
 * разработчика, а рядом — машинный `code`. Экран, показывавший `detail` как
 * есть, печатал русскому пользователю «This psychologist is not taking a request
 * from you» (так было в девятнадцати местах). Здесь код превращается в фразу на
 * языке интерфейса; незнакомый код по-русски даёт общее «не получилось», а
 * по-английски — фразу сервера: она английская и конкретнее общей.
 *
 * Словарь — зеркало кодов, которые отдают `ApiExceptions` и обработчик
 * `GlobalExceptionHandler`; новый код без строки здесь не ломает экран, он просто
 * говорит общими словами. Сторож — `error-text.test.ts`.
 */
import type { Lang } from '@/i18n/translations'
// Type-only: `client.ts` delegates `describeError` here, so a runtime import
// back into it would be a module cycle.
import type { ApiError } from './client'

type Texts = Record<string, string>

const RU: Texts = {
  VALIDATION_FAILED: 'Проверьте, как заполнены поля.',
  NAME_INVALID: 'Имя — от 1 до 200 символов.',
  OWN_PROFILE: 'Нельзя написать или записаться к самому себе.',
  EMPTY_MESSAGE: 'Сообщение пустое.',
  CURSOR_INVALID: 'Список устарел — обновите страницу.',
  MEETING_URL_INVALID: 'Проверьте ссылку на встречу: нужен адрес вида https://zoom.us/…',
  SLOT_ENDS_BEFORE_START: 'Конец часа раньше начала.',
  SLOT_TOO_LONG: 'Час не может быть длиннее четырёх часов.',
  SLOT_IN_PAST: 'Это время уже прошло.',
  COUNTRY_INVALID: 'Выберите страну из списка.',
  TIMEZONE_INVALID: 'Выберите часовой пояс из списка.',
  LANG_INVALID: 'Язык интерфейса — русский или английский.',
  FORMAT_REQUIRED: 'Выберите формат: онлайн, очно или оба.',
  SPECIALTY_UNKNOWN: 'Такой специализации нет в списке.',
  LANGUAGE_UNSUPPORTED: 'Этот язык пока не поддерживается.',
  CURRENCY_UNSUPPORTED: 'Эта валюта пока не поддерживается.',
  USERNAME_INVALID: 'Такое имя взять нельзя: 3–24 знака, латиница, цифры, точка и подчёркивание, и не из списка занятых площадкой.',
  USERNAME_REQUIRED: 'Сначала выберите имя пользователя — без него у страницы нет адреса.',
  PROFILE_INCOMPLETE: 'Для публикации нужны строка «коротко о себе» и хотя бы одна специализация.',
  EMAIL_NOT_VERIFIED: 'Сначала подтвердите адрес почты — ссылка в письме.',
  CONSENT_REQUIRED: 'Отметьте согласие, чтобы продолжить.',
  NOT_ELIGIBLE: 'Отзыв можно оставить после проведённой сессии.',
  PSYCHOLOGIST_NOT_FOUND: 'Психолог не найден — возможно, профиль снят с публикации.',
  CONNECTION_NOT_FOUND: 'Связь с психологом не найдена.',
  CONVERSATION_NOT_FOUND: 'Переписка не найдена.',
  CLIENT_NOT_FOUND: 'Клиент не найден.',
  SLOT_NOT_FOUND: 'Этого часа уже нет — выберите другой.',
  SESSION_NOT_FOUND: 'Сессия не найдена.',
  REVIEW_NOT_FOUND: 'Отзыв не найден.',
  USER_NOT_FOUND: 'Такой страницы нет.',
  PERSON_NOT_FOUND: 'Карточка не найдена.',
  DECLINED: 'Психолог сейчас не может взять вас в работу.',
  CONNECTION_CLOSED: 'Работа с этим психологом завершена или отклонена.',
  CONNECTION_NOT_ACTIVE: 'Связь с клиентом ещё не активна.',
  CONVERSATION_CLOSED: 'Переписка закрыта.',
  INTRO_LIMIT: 'Дождитесь ответа психолога, прежде чем писать снова.',
  SLOT_TAKEN: 'Этот час только что заняли — выберите другой.',
  SLOT_TOO_SOON: 'Записаться можно не позже чем за два часа до начала.',
  TOO_MANY_PENDING: 'У вас уже две неподтверждённые заявки к этому психологу — дождитесь ответа.',
  SLOT_BOOKED: 'На этот час записана сессия — сначала отмените её.',
  SLOT_OVERLAP: 'Этот час пересекается с уже открытым.',
  SESSION_WRONG_STATE: 'Сессия уже изменилась — обновите страницу.',
  SESSION_CLOSED: 'Эта сессия уже закрыта.',
  SESSION_NOT_STARTED: 'Отметить встречу можно, когда она начнётся.',
  SESSION_STARTED: 'Встреча уже началась — перенести её нельзя.',
  REVIEW_WINDOW_CLOSED:
    'Окно для отзыва закрылось: его можно оставить в течение 90 дней после последней сессии.',
  OWN_REVIEW: 'Нельзя пожаловаться на собственный отзыв.',
  ALREADY_REPORTED: 'Вы уже пожаловались на этот отзыв.',
  HANDLE_CONFUSABLE: 'Это имя слишком похоже на чужое — выберите другое.',
  HANDLE_TAKEN: 'Этот адрес уже занят.',
  HANDLE_READ_ONLY: 'Адрес страницы меняется отдельно — в поле адреса.',
  HANDLE_CHANGE_COOLDOWN: 'Адрес можно менять раз в 30 дней.',
  HANDLE_CHANGE_EXHAUSTED: 'Смены адреса закончились.',
  RATE_LIMITED: 'Слишком много попыток — подождите немного.',
}

const EN: Texts = {
  VALIDATION_FAILED: 'Please check the fields.',
  NAME_INVALID: 'The name must be 1 to 200 characters long.',
  OWN_PROFILE: 'You cannot write to or book yourself.',
  EMPTY_MESSAGE: 'The message is empty.',
  CURSOR_INVALID: 'The list is out of date — reload the page.',
  MEETING_URL_INVALID: 'Check the meeting link: it should look like https://zoom.us/…',
  SLOT_ENDS_BEFORE_START: 'The slot ends before it starts.',
  SLOT_TOO_LONG: 'A slot cannot be longer than four hours.',
  SLOT_IN_PAST: 'That time has already passed.',
  COUNTRY_INVALID: 'Choose a country from the list.',
  TIMEZONE_INVALID: 'Choose a time zone from the list.',
  LANG_INVALID: 'The interface language is Russian or English.',
  FORMAT_REQUIRED: 'Choose a format: online, in person or both.',
  SPECIALTY_UNKNOWN: 'That specialization is not on the list.',
  LANGUAGE_UNSUPPORTED: 'That language is not supported yet.',
  CURRENCY_UNSUPPORTED: 'That currency is not supported yet.',
  USERNAME_INVALID: 'That name cannot be taken: 3–24 characters, Latin letters, digits, dots and underscores, and not one the platform keeps.',
  USERNAME_REQUIRED: 'Choose a username first — the page has no address without one.',
  PROFILE_INCOMPLETE: 'To publish, add a headline and at least one specialization.',
  EMAIL_NOT_VERIFIED: 'Confirm your email address first — the link is in the email.',
  CONSENT_REQUIRED: 'Tick the consent to continue.',
  NOT_ELIGIBLE: 'You can leave a review after a completed session.',
  PSYCHOLOGIST_NOT_FOUND: 'Psychologist not found — the profile may have been unpublished.',
  CONNECTION_NOT_FOUND: 'Connection not found.',
  CONVERSATION_NOT_FOUND: 'Conversation not found.',
  CLIENT_NOT_FOUND: 'Client not found.',
  SLOT_NOT_FOUND: 'That slot is gone — pick another one.',
  SESSION_NOT_FOUND: 'Session not found.',
  REVIEW_NOT_FOUND: 'Review not found.',
  USER_NOT_FOUND: 'There is no such page.',
  PERSON_NOT_FOUND: 'Card not found.',
  DECLINED: 'This psychologist cannot take you on right now.',
  CONNECTION_CLOSED: 'Work with this psychologist has ended or was declined.',
  CONNECTION_NOT_ACTIVE: 'This client connection is not active yet.',
  CONVERSATION_CLOSED: 'This conversation is closed.',
  INTRO_LIMIT: 'Wait for the psychologist to reply before writing again.',
  SLOT_TAKEN: 'Someone has just taken that slot — pick another one.',
  SLOT_TOO_SOON: 'Bookings close two hours before the start.',
  TOO_MANY_PENDING: 'You already have two pending requests with this psychologist — wait for an answer.',
  SLOT_BOOKED: 'A session is booked into this slot — cancel it first.',
  SLOT_OVERLAP: 'This slot overlaps one you already opened.',
  SESSION_WRONG_STATE: 'The session has changed — reload the page.',
  SESSION_CLOSED: 'This session is already closed.',
  SESSION_NOT_STARTED: 'You can mark the session once it has started.',
  SESSION_STARTED: 'The session has already started, so it cannot be moved.',
  REVIEW_WINDOW_CLOSED: 'The review window has closed: reviews can be left within 90 days of the last session.',
  OWN_REVIEW: 'You cannot report your own review.',
  ALREADY_REPORTED: 'You have already reported this review.',
  HANDLE_CONFUSABLE: 'That name looks too much like somebody else’s — pick another.',
  HANDLE_TAKEN: 'That address is taken.',
  HANDLE_READ_ONLY: 'The page address is changed separately, in the address field.',
  HANDLE_CHANGE_COOLDOWN: 'The address can be changed once every 30 days.',
  HANDLE_CHANGE_EXHAUSTED: 'You have used all address changes.',
  RATE_LIMITED: 'Too many attempts — please wait a moment.',
}

const GENERIC = {
  ru: { failed: 'Не получилось. Попробуйте ещё раз.', offline: 'Сервис недоступен — попробуйте ещё раз.' },
  en: { failed: 'That did not work. Please try again.', offline: 'The service is unavailable — please try again.' },
} as const

/** Known codes, for the guard test. */
export const ERROR_CODES = Object.keys(RU)

export function errorTexts(lang: Lang): Texts {
  return lang === 'ru' ? RU : EN
}

function asApiError(error: unknown): ApiError | null {
  return error instanceof Error && error.name === 'ApiError' && typeof (error as ApiError).status === 'number'
    ? (error as ApiError)
    : null
}

/**
 * The sentence for a failed call. `fallback` is the screen's own words for a
 * failure it can name better than «did not work» (e.g. «Не удалось сохранить»).
 */
export function errorText(error: unknown, lang: Lang, fallback?: string): string {
  const generic = GENERIC[lang === 'ru' ? 'ru' : 'en']
  const apiError = asApiError(error)
  if (!apiError) return fallback ?? generic.failed
  const known = apiError.code ? errorTexts(lang)[apiError.code] : undefined
  if (known) return known
  // 429 is «transient» to the retry logic, but to a person it is «slow down»,
  // not «the service is down».
  if (apiError.status === 429) return errorTexts(lang).RATE_LIMITED
  if (apiError.isTransient) return generic.offline
  if (lang === 'en' && apiError.message && !/^HTTP \d+$/.test(apiError.message)) return fallback ?? apiError.message
  return fallback ?? generic.failed
}
